import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { X, Loader2, Warehouse } from 'lucide-react';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import {
  createAssignment,
  updateAssignment,
  fetchWarehouseOptions,
} from '../../../../../features/zones/zonesThunks';
import { SHIPPING_METHOD_OPTIONS } from '../../../../../utils/catalogs';

const inputClass =
  'w-full bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl px-4 py-2.5 text-sm text-ninja-dark dark:text-white outline-none focus:border-ninja-green transition-colors disabled:opacity-60 disabled:cursor-not-allowed';

const labelClass = 'block text-sm font-semibold text-ninja-dark dark:text-white mb-1.5';

/**
 * Links a warehouse to the zone under a given shipping method.
 *
 * Editing an existing link only changes the shipping method — the warehouse is
 * the identity of the assignment, so it stays locked, and the PATCH carries
 * just the method.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {number|string} props.zoneId
 * @param {object|null} props.assignment - Existing assignment when editing
 * @param {() => void} props.onSaved
 */
export const AssignWarehouseModal = ({ isOpen, onClose, zoneId, assignment, onSaved }) => {
  const dispatch = useDispatch();
  const { warehouseOptions, loadingWarehouses, savingAssignment } = useSelector(
    (state) => state.zones
  );

  const [warehouseId, setWarehouseId] = useState('');
  const [shippingMethod, setShippingMethod] = useState('');
  const [submitError, setSubmitError] = useState(null);

  const isEditMode = Boolean(assignment);

  useEffect(() => {
    if (!isOpen) return;

    dispatch(fetchWarehouseOptions());
    setSubmitError(null);
    setWarehouseId(assignment?.warehouse_id ?? assignment?.warehouse ?? '');
    setShippingMethod(assignment?.shipping_method || '');
  }, [isOpen, assignment, dispatch]);

  if (!isOpen) return null;

  const isFormValid = isEditMode ? Boolean(shippingMethod) : Boolean(warehouseId && shippingMethod);

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!isFormValid) return;

    setSubmitError(null);

    const action = isEditMode
      ? updateAssignment({
          assignmentId: assignment.id,
          assignmentData: { shipping_method: shippingMethod },
        })
      : createAssignment({
          zoneId,
          assignmentData: { warehouse: warehouseId, shipping_method: shippingMethod },
        });

    dispatch(action)
      .unwrap()
      .then(() => {
        onSaved?.();
        onClose();
      })
      .catch((error) => {
        setSubmitError(error || 'No se pudo guardar la asignación');
      });
  };

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      {/* Not closable while saving: the outcome, or the error, lands in here. */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={savingAssignment ? undefined : onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-busy={savingAssignment}
        data-testid="assign-warehouse-modal"
        className="relative bg-white dark:bg-[#1a1a1a] rounded-3xl w-full max-w-lg shadow-2xl border border-border animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-start justify-between gap-4 p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-ninja-greenLight flex items-center justify-center text-ninja-green">
              <Warehouse size={20} />
            </div>
            <div>
              <h3 className="text-xl font-bold text-ninja-dark dark:text-white">
                {isEditMode ? 'Editar Asignación' : 'Asignar Bodega'}
              </h3>
              <p className="text-sm text-text-secondary">
                Define con qué método de envío opera esta bodega en la zona.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={savingAssignment}
            aria-label="Cerrar"
            className="text-text-muted hover:text-ninja-dark dark:hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-5">
            <div>
              {isEditMode ? (
                <>
                  <label className={labelClass}>Bodega (Cliente)</label>
                  <div className={`${inputClass} opacity-60 cursor-not-allowed`}>
                    {assignment.warehouse_name}
                    {assignment.company_name ? ` (${assignment.company_name})` : ''}
                  </div>
                </>
              ) : (
                <FilterableSelect
                  variant="form"
                  label="Bodega (Cliente)"
                  value={warehouseId}
                  onChange={setWarehouseId}
                  options={warehouseOptions}
                  disabled={loadingWarehouses}
                  placeholder={loadingWarehouses ? 'Cargando bodegas...' : 'Seleccionar...'}
                  searchPlaceholder="Buscar bodega o cliente..."
                />
              )}
            </div>

            <div>
              <FilterableSelect
                variant="form"
                label="Método de Envío"
                value={shippingMethod}
                onChange={setShippingMethod}
                options={SHIPPING_METHOD_OPTIONS}
                searchPlaceholder="Buscar método de envío..."
              />
            </div>

            {submitError && (
              <div className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm">
                {submitError}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              disabled={savingAssignment}
              className="px-5 py-2.5 rounded-xl border border-border text-text-secondary hover:text-ninja-dark dark:hover:text-white hover:border-ninja-dark dark:hover:border-white font-semibold text-sm transition-colors disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!isFormValid || savingAssignment}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-ninja-green hover:bg-ninja-green/90 text-white font-bold text-sm transition-all disabled:opacity-50"
            >
              {savingAssignment && <Loader2 size={16} className="animate-spin" />}
              {savingAssignment ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};
